/**
 * The "Not verifiable" predicate and the Squad Cost Ratio slot's rule
 * selector (SE-8798).
 *
 * Both answer a question every surface showing a squad-cost rule asks, so they
 * live in one place rather than being re-derived per surface:
 *
 * - Which rule does the Squad Cost Ratio slot show when a club holds two of
 *   the squad-cost family? The KPI card, its Edit pencil, the drill-through,
 *   the PSR card's scenario delta and the scenario overlay all resolve it
 *   here, so none of them can show a different rule from the card.
 * - Is a rule one the app cannot verify at all (League One/Two SCMP, which
 *   needs figures the app has no entry point for)?
 * @module features/financial-compliance/domain/squadCostEvaluation
 */
import {
  CompliancePartialReason,
  ComplianceRuleFamily,
  ComplianceRuleLayer,
  ComplianceRuleState,
} from "@/generated/graphql";

/** The fields {@link preferredSquadCostEvaluation} reads off an evaluation. */
export interface SquadCostCandidate {
  /** The cross-regime family; null for a rule outside every family. */
  readonly ruleFamily?: ComplianceRuleFamily | null;
  /** The regime layer the rule belongs to. */
  readonly layer: ComplianceRuleLayer;
  /** The rule's EFFECTIVE warn-only flag for this club (SE-8791). */
  readonly warnOnly: boolean;
}

/** The fields {@link isNotVerifiable} reads off an evaluation. */
export interface VerifiabilityCandidate {
  /** The rule's state. */
  readonly state: ComplianceRuleState;
  /** Why the rule withheld a verdict, if it did. */
  readonly partialReasons: readonly CompliancePartialReason[];
}

/**
 * Whether a rule is one the app cannot verify (SE-8798): PARTIAL, with
 * `NOT_VERIFIABLE` among its reasons. Such a rule shows no number and no
 * colour, and the backend already leaves it out of the alert and breach
 * counts. It is not "awaiting figures": the club has no field to enter them.
 * @param evaluation - The rule.
 * @returns True when the rule cannot be verified.
 */
export const isNotVerifiable = (evaluation: VerifiabilityCandidate): boolean =>
  evaluation.state === ComplianceRuleState.Partial &&
  evaluation.partialReasons.includes(CompliancePartialReason.NotVerifiable);

/**
 * Whether a rule belongs to the squad-cost family.
 * @param evaluation - The rule.
 * @returns True for a squad-cost-control rule.
 */
const isSquadCost = (evaluation: SquadCostCandidate): boolean =>
  evaluation.ruleFamily === ComplianceRuleFamily.SquadCostControl;

/**
 * The squad-cost rule the Squad Cost Ratio slot shows (SE-8798, option 2).
 *
 * The first squad-cost rule wins, as it always has — EXCEPT when that rule is
 * Continental and warn-only and the club also holds a Domestic squad-cost
 * rule. Then the Domestic one wins: a warning-only rule must not take the
 * headline slot from the rule the club is actually held to (Port Vale: UEFA
 * warn-only, EFL SCMP binding). The warn-only rule still appears as its own
 * Continental column row; it simply has no KPI card.
 *
 * "Manage targets & rules" deliberately does NOT use this: its Squad Cost
 * Control row names the Continental rule and keeps preferring it (SE-8682).
 * @param evaluations - Every regime-rule evaluation the read carries.
 * @returns The rule to show, or undefined when the club holds none.
 */
export const preferredSquadCostEvaluation = <T extends SquadCostCandidate>(
  evaluations: readonly T[]
): T | undefined => {
  const first = evaluations.find(isSquadCost);
  if (
    first === undefined ||
    first.layer !== ComplianceRuleLayer.Continental ||
    !first.warnOnly
  )
    return first;
  return (
    evaluations.find(
      evaluation =>
        isSquadCost(evaluation) &&
        evaluation.layer === ComplianceRuleLayer.Domestic
    ) ?? first
  );
};
